import React, { useEffect, useState } from "react";
import { BarChart3, X } from "lucide-react";
import UiText from "../components/UiText";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import api from "../api/axios";

export default function AdminAnalysisReportsPage() {
  const [stats, setStats] = useState({
    totalDeals: 0,
    totalCompanies: 0,
    totalCustomers: 0,
    totalUsers: 0,
  });
  // stagesData: counts of deals per stage (for pie)
  const [stagesData, setStagesData] = useState([]);
  // companiesData: companies with customer counts (for bar)
  const [companiesData, setCompaniesData] = useState([]);
  // revenueByCompany: companies with total revenue from deals
  const [revenueByCompany, setRevenueByCompany] = useState([]);
  const [dealStatus, setDealStatus] = useState({ closedDeals: 0, otherDeals: 0 });
  const [userDealsData, setUserDealsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // State for company deals popup
  const [allDeals, setAllDeals] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDeals, setCompanyDeals] = useState([]);
  const [deletedDeals, setDeletedDeals] = useState([]);
  const [deletedDealsLoading, setDeletedDealsLoading] = useState(false);
  const [deletedDealsError, setDeletedDealsError] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchDeletedDeals = async ({ showInlineError = true } = {}) => {
    try {
      setDeletedDealsLoading(true);
      if (showInlineError) setDeletedDealsError("");
      const deletedDealsRes = await api.get("/api/admin/deals/deleted");
      const payload = deletedDealsRes.data;
      const deleted =
        Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload?.deleted_deals)
                ? payload.deleted_deals
                : [];
      setDeletedDeals(deleted);
    } catch (err) {
      if (showInlineError) {
        const detail = err?.response?.data?.detail || "Failed to load deleted deals.";
        setDeletedDealsError(String(detail));
      }
      setDeletedDeals([]);
    } finally {
      setDeletedDealsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Avoid calling admin-only endpoints (e.g. /api/admin/users/) which return 401 for non-admins.
      const [dealsRes, companiesRes, customersRes] = await Promise.all([
        api.get("/api/deals/", { params: { limit: 5000 } }),
        api.get("/api/companies/", { params: { limit: 500 } }),
        api.get("/api/customers/", { params: { limit: 500 } }),
      ]);

      // Store all deals for later filtering
      const deals = dealsRes.data || [];
      setAllDeals(deals);
      const companies = companiesRes.data || [];
      const customers = customersRes.data || [];

      // Load deleted deals in parallel section with its own state and refresh support.
      await fetchDeletedDeals({ showInlineError: false });

      // Derive total users from deals created_by field (best-effort without admin user list)
      const uniqueUserIds = Array.from(new Set(deals.map(d => d.created_by).filter(Boolean)));

      setStats({
        totalDeals: deals.length,
        totalCompanies: companies.length,
        totalCustomers: customers.length,
        totalUsers: uniqueUserIds.length,
      });

      // Calculate deals by stage/status for pie chart
      const stageCount = {};
      deals.forEach((deal) => {
        const stage = (deal.stage || "Unknown").toString();
        stageCount[stage] = (stageCount[stage] || 0) + 1;
      });
      const stageData = Object.entries(stageCount).map(([stage, count]) => ({ name: stage, count }));
      setStagesData(stageData);

      // Calculate closed vs other deals
      const closed = deals.filter((deal) => {
        const s = (deal.stage || "").toString().toLowerCase();
        return s.includes("closed") || s.includes("won");
      }).length;
      setDealStatus({ closedDeals: closed, otherDeals: deals.length - closed });

      // Prepare companies data with customer counts (use provided counts if present; fallback by matching customers)
      const companiesWithCustomers = companies.map((c) => {
        const id = c.id ?? c.company_id;
        const count = c.customers_count ?? c.customer_count ?? customers.filter((cu) => cu.company_id === id).length;
        return { name: c.name || c.company_name || `Company ${id}`, customers: count };
      });
      setCompaniesData(companiesWithCustomers);

      // Calculate revenue by company
      const revenueMap = {};
      deals.forEach((deal) => {
        const companyName = deal.company_name || "Unknown Company";
        const revenue = parseFloat(deal.amount || 0);
        revenueMap[companyName] = (revenueMap[companyName] || 0) + revenue;
      });
      const revenueByComp = Object.entries(revenueMap)
        .map(([company, revenue]) => ({
          name: company,
          revenue: Math.round(revenue),
        }))
        .sort((a, b) => b.revenue - a.revenue);
      setRevenueByCompany(revenueByComp);

      // Deals per user for team performance chart
      // Build deals-per-user using `created_by_name` (if backend supplies) or `created_by` id
      const counts = {};
      deals.forEach((deal) => {
        const name = deal.created_by_name || deal.created_by || "Unassigned";
        counts[name] = (counts[name] || 0) + 1;
      });
      const userDeals = Object.entries(counts)
        .map(([name, dealsCount]) => ({ name, deals: dealsCount }))
        .sort((a, b) => b.deals - a.deals)
        .slice(0, 8);
      setUserDealsData(userDeals);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      setError("Failed to load analysis data");
    } finally {
      setLoading(false);
    }
  };

  // Handle company click to show deals
  const handleCompanyClick = (companyName) => {
    setSelectedCompany(companyName);
    const deals = allDeals.filter(deal => deal.company_name === companyName);
    setCompanyDeals(deals);
  };

  const closeModal = () => {
    setSelectedCompany(null);
    setCompanyDeals([]);
  };

  // Pie chart data - All Deals by Stage (from API computed `stagesData`)
  const dealStatusData = stagesData.map((stage, idx) => ({
    name: stage.name || stage.stage || `Item ${idx + 1}`,
    value: stage.count ?? stage.value ?? 0,
    fill: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#a855f7", "#06b6d4", "#ec4899"][idx % 7],
  }));

  // Bar chart data - Companies with customer counts (from API `companiesData`)
  const barChartData = companiesData.map((company) => ({
    name: company.name,
    customers: company.customers ?? 0,
  }));

  // Custom tick renderer: breaks long labels into chunks and rotates for readability
  const renderCustomizedTick = ({ x, y, payload }) => {
    const value = payload?.value || "";
    // Prefer splitting by words and wrap lines without breaking words
    const words = value.split(" ");
    const maxLineLen = 10;
    const lines = [];
    let current = "";
    words.forEach((w) => {
      if ((current + (current ? " " : "") + w).length <= maxLineLen) {
        current = current ? `${current} ${w}` : w;
      } else {
        if (current) lines.push(current);
        // if single word is longer than maxLineLen, allow it as its own line
        current = w;
      }
    });
    if (current) lines.push(current);

    const fontSize = 10;
    const lineHeight = 12;
    // rotate slightly less and anchor to middle so labels take less bottom space
    return (
      <text x={x} y={y + 10} textAnchor="middle" transform={`rotate(-30 ${x} ${y + 10})`} style={{ fontSize }}>
        {lines.map((c, i) => (
          <tspan key={i} x={x} dy={i === 0 ? 0 : lineHeight}>
            {c}
          </tspan>
        ))}
      </text>
    );
  };

  // Simulated trend data for line chart
  // NOTE: This shows a calculated growth trajectory reaching current totals by month end.
  // Historical monthly aggregations are not stored; use created_at timestamps for actual historical analysis.
  const trendData = [
    { month: "Jan", deals: Math.floor(stats.totalDeals * 0.3), companies: Math.floor(stats.totalCompanies * 0.2), customers: Math.floor(stats.totalCustomers * 0.4) },
    { month: "Feb", deals: Math.floor(stats.totalDeals * 0.5), companies: Math.floor(stats.totalCompanies * 0.4), customers: Math.floor(stats.totalCustomers * 0.6) },
    { month: "Mar", deals: Math.floor(stats.totalDeals * 0.7), companies: Math.floor(stats.totalCompanies * 0.6), customers: Math.floor(stats.totalCustomers * 0.8) },
    { month: "Apr", deals: Math.floor(stats.totalDeals * 0.85), companies: Math.floor(stats.totalCompanies * 0.75), customers: Math.floor(stats.totalCustomers * 0.9) },
    { month: "May", deals: Math.floor(stats.totalDeals * 0.95), companies: Math.floor(stats.totalCompanies * 0.85), customers: Math.floor(stats.totalCustomers * 0.95) },
    { month: "Jun", deals: stats.totalDeals, companies: stats.totalCompanies, customers: stats.totalCustomers },
  ];

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <p className="text-gray-400">Loading analysis data...</p>
        </div>
      ) : (
        <>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 size={32} className="text-blue-600" />
          <UiText variant="title" as="h1">Analysis & Reports</UiText>
        </div>
        <p className="text-gray-600">Visual overview of system metrics and performance data</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          {error}
        </div>
      )}

      {/* Top totals removed as requested */}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Pie Chart - All Deals by Stage */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <UiText variant="section" as="h2" className="mb-3">All Deals Distribution</UiText>
          {dealStatusData.some(d => d.value > 0) ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={dealStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {dealStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value} deals`} />
                </PieChart>
              </ResponsiveContainer>
              {/* compact legend box below the chart for labels */}
              <div style={{ width: "100%", maxWidth: 520, marginTop: 4, padding: 6, borderRadius: 6, background: "#ffffff", boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.02)" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
                  {dealStatusData.map((d, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#374151" }}>
                      <div style={{ width: 10, height: 10, background: d.fill, borderRadius: 3 }} />
                      <div style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden", maxWidth: 140 }}>{d.name}: {d.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              No deal data available
            </div>
          )}
        </div>

        {/* Bar Chart - Revenue by Company */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <UiText variant="section" as="h2" className="mb-3">Revenue by Company</UiText>
          {revenueByCompany.length > 0 && revenueByCompany.some(r => r.revenue > 0) ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={revenueByCompany} margin={{ left: 20, right: 12, top: 6, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={renderCustomizedTick} height={60} interval={0} />
                <YAxis 
                  width={50} 
                  tick={{ fontSize: 9 }} 
                  label={{ value: "Revenue ($)", angle: -90, position: "insideLeft", offset: -6, style: { fontSize: 9 } }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value) => `$${parseFloat(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                  labelFormatter={(label) => `${label}`}
                />
                <Bar dataKey="revenue" fill="#06b6d4" name="Revenue" barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              No revenue data available
            </div>
          )}
        </div>
      </div>

      {/* Second Row - Companies & Customers and Revenue Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Bar Chart - Companies and Customers */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <UiText variant="section" as="h2" className="mb-3">Companies & Customers</UiText>
          {barChartData.length > 0 && barChartData.some(b => (b.customers ?? 0) > 0) ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={barChartData} margin={{ left: 20, right: 12, top: 6, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={renderCustomizedTick} height={60} interval={0} />
                <YAxis width={36} tick={{ fontSize: 9 }} label={{ value: "Number of Customers", angle: -90, position: "insideLeft", offset: -6, style: { fontSize: 9 } }} />
                <Tooltip />
                <Bar dataKey="customers" fill="#10b981" name="Customers" barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              No company data available
            </div>
          )}
        </div>

        {/* Revenue Summary Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <UiText variant="section" as="h2" className="mb-3">Top Revenue Generators</UiText>
          {revenueByCompany.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {revenueByCompany.slice(0, 10).map((item, idx) => (
                <div 
                  key={idx} 
                  onClick={() => handleCompanyClick(item.name)}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-cyan-50 hover:shadow-md transition cursor-pointer"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-cyan-700">{idx + 1}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 truncate">{item.name}</span>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <span className="text-sm font-bold text-cyan-600">
                      ${parseFloat(item.revenue).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              No revenue data available
            </div>
          )}
        </div>
      </div>

      {/* Third Row - Team Performance and Growth Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* User Performance Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <UiText variant="section" as="h2" className="mb-3">Team Performance - Deals per User</UiText>
          {userDealsData.length > 0 && userDealsData.some(u => u.deals > 0) ? (
              <ResponsiveContainer width="100%" height={240}>
              <BarChart data={userDealsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="deals" fill="#a855f7" name="Deals Handled" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              No user performance data available
            </div>
          )}
        </div>

        {/* Trend Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <UiText variant="section" as="h2" className="mb-3">6-Month Growth Trend</UiText>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="deals" stroke="#3b82f6" strokeWidth={2} name="Deals" dot={{ fill: "#3b82f6", r: 3 }} />
              <Line type="monotone" dataKey="companies" stroke="#10b981" strokeWidth={2} name="Companies" dot={{ fill: "#10b981", r: 3 }} />
              <Line type="monotone" dataKey="customers" stroke="#a855f7" strokeWidth={2} name="Customers" dot={{ fill: "#a855f7", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <UiText variant="section" as="h2" className="mb-3">Key Insights</UiText>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="font-semibold text-blue-900 mb-1">💼 Deal Pipeline</p>
            <p className="text-sm text-blue-800">
              {dealStatus.closedDeals} closed deals out of {stats.totalDeals} total ({stats.totalDeals > 0 ? ((dealStatus.closedDeals / stats.totalDeals) * 100).toFixed(0) : 0}% success rate)
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="font-semibold text-green-900 mb-1">🏢 Portfolio</p>
            <p className="text-sm text-green-800">Managing {stats.totalCompanies} companies with {stats.totalCustomers} contacts</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="font-semibold text-purple-900 mb-1">👥 Team Members</p>
            <p className="text-sm text-purple-800">{stats.totalUsers} users actively working on deals</p>
          </div>
        </div>
      </div>

      {/* Deleted Deals Audit */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
        <div className="flex items-center justify-between mb-4">
          <UiText variant="section" as="h2">Deleted Deals</UiText>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fetchDeletedDeals({ showInlineError: true })}
              disabled={deletedDealsLoading}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {deletedDealsLoading ? "Refreshing..." : "Refresh"}
            </button>
            <span className="text-sm text-gray-500">{deletedDeals.length} deleted</span>
          </div>
        </div>

        {deletedDealsError ? (
          <div className="mb-3 text-sm text-red-600">{deletedDealsError}</div>
        ) : null}

        {deletedDealsLoading ? (
          <div className="h-32 flex items-center justify-center text-gray-400">
            Loading deleted deals...
          </div>
        ) : deletedDeals.length === 0 ? (
          <div className="h-32 flex items-center justify-center text-gray-400">
            No deleted deals found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b border-gray-200">
                  <th className="py-2 pr-4 font-semibold">Deal</th>
                  <th className="py-2 pr-4 font-semibold">Company</th>
                  <th className="py-2 pr-4 font-semibold">Deleted By</th>
                  <th className="py-2 pr-4 font-semibold">Deleted At</th>
                  <th className="py-2 pr-4 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {deletedDeals.map((deal) => (
                  <tr key={deal.id} className="border-b border-gray-100 align-top">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-gray-900">{deal.title}</div>
                      <div className="text-xs text-gray-500">${parseFloat(deal.amount || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                    </td>
                    <td className="py-3 pr-4 text-gray-700">{deal.company_name || "-"}</td>
                    <td className="py-3 pr-4 text-gray-700">{deal.deleted_by_name || deal.deleted_by_id || "Unknown"}</td>
                    <td className="py-3 pr-4 text-gray-700">
                      {deal.deleted_at ? new Date(deal.deleted_at).toLocaleString() : "-"}
                    </td>
                    <td className="py-3 pr-4 text-gray-700 whitespace-pre-wrap">{deal.deletion_reason || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Company Deals Popup Modal */}
      {selectedCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-cyan-50 to-blue-50">
              <div>
                <UiText variant="section" as="h2" className="text-lg font-semibold text-gray-900">
                  {selectedCompany}
                </UiText>
                <p className="text-sm text-gray-600 mt-1">{companyDeals.length} deal{companyDeals.length !== 1 ? 's' : ''} totaling ${companyDeals.reduce((sum, deal) => sum + parseFloat(deal.amount || 0), 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-200 rounded-lg transition"
              >
                <X size={24} className="text-gray-700" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 p-6">
              {companyDeals.length > 0 ? (
                <div className="space-y-3">
                  {companyDeals.map((deal) => (
                    <div key={deal.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <UiText variant="section" as="h3" className="font-semibold text-gray-900">
                            {deal.title}
                          </UiText>
                          <p className="text-xs text-gray-500 mt-1">Stage: <span className="font-medium text-gray-700">{deal.stage || 'Unknown'}</span></p>
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <div className="px-3 py-1 bg-emerald-100 rounded-lg">
                            <span className="text-sm font-bold text-emerald-700">
                              ${parseFloat(deal.amount || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-4 text-xs text-gray-600">
                        <div>
                          <span className="text-gray-500">Close Date:</span> {deal.close_date ? new Date(deal.close_date).toLocaleDateString() : 'No date'}
                        </div>
                        <div>
                          <span className="text-gray-500">Updated:</span> {deal.updated_at ? new Date(deal.updated_at).toLocaleDateString() : 'Recently'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-40 text-gray-400">
                  <p>No deals found for this company</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
